import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  getLinkedinAccountConnections,
  LinkedinConnectionItem,
  LinkedinConnectionsResponse,
  requestLinkedinAccountConnectionsRefresh,
} from 'API/linkedin-accounts';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiDotLoader from 'features/ui/base-components/pai-dot-loader';
import PaiPagination from 'features/ui/base-components/pai-pagination';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { formatDateTime } from 'utils/date.utils';
import { LinkedinAccount } from './linkedin-accounts.types';

interface Props {
  open: boolean;
  account: LinkedinAccount | null;

  handleClose(): void;
}

const PAGE_SIZE = 25;

const connectionLabel = (item: LinkedinConnectionItem): string => {
  const name = `${item.contact?.firstName || ''} ${item.contact?.lastName || ''}`.trim();
  return name || item.vanityName || item.profileId || '';
};

/**
 * Kontaktliste eines Kontos: alle Verbindungen ersten Grades, wie der Worker sie aus der
 * App liest und speichert. Namen erscheinen, sobald die Verbindung einem Kontakt des
 * Mandanten zugeordnet ist; sonst der LinkedIn-Kurzname.
 */
const AccountConnectionsDialog: React.FC<Props> = ({ open, account, handleClose }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState<LinkedinConnectionsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const accountId = account?._id;

  useEffect(() => {
    if (!open) {
      setQuery('');
      setPage(1);
      setData(null);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !accountId) return;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true);
      const res = await getLinkedinAccountConnections(
        accountId,
        { q: query.trim() || undefined, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE },
        controller.signal,
      );
      if (controller.signal.aborted) return;
      setLoading(false);
      if ('error' in res) {
        setError(t('accountConnectionsLoadError'));
        return;
      }
      setError(null);
      setData(res);
    }, query ? 300 : 0);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, accountId, query, page, t]);

  const handleRefresh = async () => {
    if (!accountId) return;
    setRefreshing(true);
    const res = await requestLinkedinAccountConnectionsRefresh(accountId);
    setRefreshing(false);
    if (!_.isNil(res?.error)) {
      setError(t('accountConnectionsRefreshError'));
      return;
    }
    setData((prev) => (prev ? { ...prev, refresh: { requestedPages: 300, nextPage: 0 } } : prev));
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} fullWidth={true} maxWidth={'md'} transitionDuration={0} onClose={handleClose}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('accountConnections')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={2}>
          <Typography className={classes.text}>
            {t('accountConnectionsIntro', { userName: account?.userEmail || '' })}
          </Typography>
          <Stack direction={'row'} gap={2} alignItems={'center'} flexWrap={'wrap'}>
            <PaiTextField
              value={query}
              placeholder={t('accountConnectionsSearchPlaceholder')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setQuery(e.target.value);
                setPage(1);
              }}
            />
            <Typography className={classes.meta}>
              {data
                ? t('accountConnectionsCount', { count: data.total })
                : ''}
            </Typography>
            <Typography className={classes.meta}>
              {data?.lastCrawlAt ? t('accountConnectionsLastRead', { date: formatDateTime(data.lastCrawlAt, 'dd.MM.yyyy HH:mm') }) : t('accountConnectionsNeverRead')}
            </Typography>
          </Stack>
          {data?.refresh ? (
            <Typography className={classes.meta}>
              {t('accountConnectionsRefreshRunning', { page: data.refresh.nextPage, pages: data.refresh.requestedPages })}
            </Typography>
          ) : null}
          {error ? <Typography className={classes.error}>{error}</Typography> : null}
          {loading && !data ? (
            <PaiDotLoader />
          ) : (
            <div className={classes.tableWrap}>
              <Table size={'small'} stickyHeader={true}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('accountConnectionsColumnName')}</TableCell>
                    <TableCell>{t('accountConnectionsColumnCompany')}</TableCell>
                    <TableCell>{t('accountConnectionsColumnFirstSeen')}</TableCell>
                    <TableCell>{t('accountConnectionsColumnInContacts')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.items || []).map((item) => (
                    <TableRow key={item._id}>
                      <TableCell>
                        {item.linkedinUrl ? (
                          <Link href={item.linkedinUrl} target={'_blank'} rel={'noreferrer'} underline={'hover'}>
                            {connectionLabel(item)}
                          </Link>
                        ) : (
                          connectionLabel(item)
                        )}
                        {item.contact?.headline ? <Typography className={classes.headline}>{item.contact.headline}</Typography> : null}
                      </TableCell>
                      <TableCell>{item.contact?.company || ''}</TableCell>
                      <TableCell>{formatDateTime(item.firstSeenAt, 'dd.MM.yyyy')}</TableCell>
                      <TableCell>{item.contact ? t('yes') : t('no')}</TableCell>
                    </TableRow>
                  ))}
                  {data && data.items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography className={classes.meta}>{t('accountConnectionsEmpty')}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </div>
          )}
          {data && totalPages > 1 ? (
            <PaiPagination page={page} totalPages={totalPages} handleChangePage={(_e: unknown, value: number) => setPage(value)} />
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('close')} size={'sm'} underline={'always'} action={handleClose} />
        <PaiButton
          text={t('accountConnectionsRefresh')}
          size={'small'}
          border={'rounded'}
          startIcon={<RefreshIcon fontSize={'small'} />}
          action={handleRefresh}
          disabled={refreshing || !_.isNil(data?.refresh)}
        />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: `linear-gradient(to right, ${theme.palette.info.main}, ${theme.palette.info.light})`,
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
  },
  meta: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  headline: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.greys.medium,
  },
  error: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.error.main,
  },
  tableWrap: {
    maxHeight: '50vh',
    overflow: 'auto',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default AccountConnectionsDialog;
